import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { FALLBACK_COLOR, hexToRgba } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
/**
 * SquareRootBuilderRenderer — Build an N×N square to match a target area.
 *
 * Sprint R1 fixes:
 * - F1: honour moduleColor for selection/labels instead of hardcoded blue
 * - F2: use shouldValidate rising-edge protocol (one onAnswer emit per round)
 *
 * Config (data.squareRootBuilder):
 *   - targetValue: number (the area the student must reach)
 *   - maxSize?: number (grid size, default 10)
 */
export const SquareRootBuilderRenderer: React.FC<QuestionRendererProps> = ({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}) => {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data?.squareRootBuilder;
  const hasConfig = !!config && typeof config.targetValue === 'number';
  const targetValue = config?.targetValue ?? 16;
  const maxSize = Math.min(config?.maxSize ?? 10, 20);
  const [selectedSize, setSelectedSize] = useState<number>(0);
  // Reset on question change
  useEffect(() => {
    setSelectedSize(0);
  }, [question.id]);
  // Track ready state
  useEffect(() => {
    onReadyChange?.(selectedSize > 0);
  }, [selectedSize, onReadyChange]);
  // F2 — REN-1: edge-triggered submit via shared hook.
  useEdgeTriggeredSubmit(selectedSize, shouldValidate, onAnswer, question.id);
  // Surface missing config to the seed-author instead of silently
  // falling back to √16 (F8 partial fix)
  if (!hasConfig) {
    return <div className="text-center p-8 bg-amber-50 rounded-xl border-2 border-amber-200" data-id="element-12539" data-mp="2seqhkmwe7gw0">
        <p className="text-amber-700 font-bold" data-id="element-12540" data-mp="2oezx10xglwzx">
          ⚠️ Configuratie ontbreekt (squareRootBuilder)
        </p>
        <p className="text-sm text-amber-600 mt-2" data-id="element-12541" data-mp="y1iuxiikmblg">
          Deze vraag mist <code data-id="element-12542" data-mp="u0p70p54owsk">data.squareRootBuilder.targetValue</code>.
        </p>
      </div>;
  }
  const displaySize = selectedSize;
  // Sprint R2 F7: responsive cell size for mobile touch targets.
  // clamp(28px, 8vw, 40px) keeps the 10×10 grid within ~282-402px wide,
  // safely fitting 360px+ phones while reaching tap-friendly size on tablet.
  const gap = 2;
  const cellSizeCss = 'clamp(36px, 9vw, 48px)';
  const activeCellStyle: React.CSSProperties = {
    backgroundColor: hexToRgba(color, 0.18),
    borderColor: color
  };
  return <div className="flex flex-col items-center justify-center space-y-8 py-4" data-id="element-12543" data-mp="3c2qdqacppd2d">
      <div className="text-center" data-id="element-12544" data-mp="3h11qasue61oa">
        <p className="text-lg font-medium text-slate-700" data-id="element-12545" data-mp="3h09wnnkcue0n">
          Bouw een vierkant met een oppervlakte van{' '}
          <span className="font-bold" style={{
          color
        }} data-id="element-12546" data-mp="3qs62jkson8ff">
            
            {targetValue}
          </span>
        </p>
        <p className="text-sm text-slate-500" data-id="element-12547" data-mp="3vsaecxinbezb">
          Tik in het raster om de zijde te kiezen
        </p>
      </div>

      <div className="relative" data-id="element-12548" data-mp="3npy4funrwvj">
        {/* Top Label */}
        <div className="absolute -top-8 left-0 flex w-full justify-center text-lg font-bold transition-all" style={{
        color: displaySize > 0 ? color : '#475569'
      }} data-id="element-12549" data-mp="1o5r09ad36q3o">
          
          {displaySize > 0 ? displaySize : '?'}
        </div>

        {/* Left Label */}
        <div className="absolute -left-8 top-0 flex h-full items-center text-lg font-bold transition-all" style={{
        color: displaySize > 0 ? color : '#475569'
      }} data-id="element-12550" data-mp="1a5zeuok8q7fn">
          
          {displaySize > 0 ? displaySize : '?'}
        </div>

        {/* Interactive Grid */}
        <div className="grid cursor-pointer max-w-[min(420px,90vw)] touch-manipulation" style={{
        gridTemplateColumns: `repeat(${maxSize}, ${cellSizeCss})`,
        gap: `${gap}px`
      }} data-id="element-12551" data-mp="22wra9jthes1x">
          
          {Array.from({
          length: maxSize * maxSize
        }).map((_, i) => {
          const row = Math.floor(i / maxSize) + 1;
          const col = i % maxSize + 1;
          const isActive = row <= selectedSize && col <= selectedSize;
          return <div key={i} className="rounded-sm border transition-colors" style={{
            width: cellSizeCss,
            height: cellSizeCss,
            ...(isActive ? activeCellStyle : {
              backgroundColor: '#f8fafc',
              borderColor: '#e2e8f0'
            })
          }} onClick={() => setSelectedSize(Math.max(row, col))} data-id="element-12552" data-mp="269ofstvgv96u" data-mpi={i} />;
        })}
        </div>

        {/* Center Area Label */}
        {displaySize > 0 && <div className="pointer-events-none absolute inset-0 flex items-center justify-center" data-id="element-12553" data-mp="2p43h4q593dxu">
            <motion.div key={displaySize} initial={{
          scale: 0.5,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 font-bold shadow-sm backdrop-blur-sm" style={{
          color
        }} data-id="element-12554" data-mp="2u2n2aoc0slp5">
            
              {displaySize * displaySize}
            </motion.div>
          </div>}
      </div>
    </div>;
};